import type { CalendarDate, CalendarDay, CalendarMonth, CalendarWeek, CalendarWeekday } from './Calendar.types'
import {
  addCalendarDays,
  makeCalendarDate,
  makeMonthKey,
  parseCalendarDate,
  toCalendarDate,
} from './calendarDate'

const DAYS_IN_WEEK = 7

export interface BuildCalendarMonthsOptions {
  from: CalendarDate
  to: CalendarDate
  /** 0 = Sunday. Defaults to Monday. */
  weekStartsOn?: CalendarWeekday
  /** `YYYY-MM` allowlist. Months outside it are omitted entirely. */
  months?: ReadonlyArray<string>
  /** Injectable for tests. Defaults to the current local date. */
  today?: CalendarDate
}

/** Which column a date falls in, given where the week starts. */
export function getWeekColumn(date: CalendarDate, weekStartsOn: CalendarWeekday): number {
  return (parseCalendarDate(date).getDay() - weekStartsOn + DAYS_IN_WEEK) % DAYS_IN_WEEK
}

function listMonthKeys(from: CalendarDate, to: CalendarDate): Array<string> {
  const start = parseCalendarDate(from)
  const end = parseCalendarDate(to)
  const keys: Array<string> = []
  let year = start.getFullYear()
  let month = start.getMonth()

  while (year < end.getFullYear() || (year === end.getFullYear() && month <= end.getMonth())) {
    keys.push(makeMonthKey(year, month))
    month += 1
    if (month > 11) {
      month = 0
      year += 1
    }
  }

  return keys
}

function buildDay(
  date: CalendarDate,
  monthIndex: number,
  from: CalendarDate,
  to: CalendarDate,
  today: CalendarDate,
): CalendarDay {
  const parsed = parseCalendarDate(date)
  return {
    date,
    year: parsed.getFullYear(),
    month: parsed.getMonth(),
    day: parsed.getDate(),
    isAdjacentMonth: parsed.getMonth() !== monthIndex,
    isOutOfBounds: date < from || date > to,
    isToday: date === today,
  }
}

function chunkIntoWeeks(days: Array<CalendarDay>): Array<CalendarWeek> {
  const weeks: Array<CalendarWeek> = []
  for (let index = 0; index < days.length; index += DAYS_IN_WEEK) {
    weeks.push({ days: days.slice(index, index + DAYS_IN_WEEK), laneCount: 0 })
  }
  return weeks
}

/**
 * Generates every month between `from` and `to`, each padded out with the
 * adjacent months' days so every week has exactly seven cells. Padding days
 * are real dates flagged as `isAdjacentMonth`, so a consumer can render them
 * faded or blank without the grid having to decide.
 */
export function buildCalendarMonths(options: BuildCalendarMonthsOptions): Array<CalendarMonth> {
  const { from, to, weekStartsOn = 1, months, today = toCalendarDate(new Date()) } = options

  if (from > to) return []

  const allowed = months ? new Set(months) : undefined

  return listMonthKeys(from, to)
    .filter(key => !allowed || allowed.has(key))
    .map((key): CalendarMonth => {
      const year = Number(key.slice(0, 4))
      const monthIndex = Number(key.slice(5, 7)) - 1

      const firstOfMonth = makeCalendarDate(year, monthIndex, 1)
      const lastOfMonth = makeCalendarDate(year, monthIndex + 1, 0)

      const leadingCount = getWeekColumn(firstOfMonth, weekStartsOn)
      const trailingCount = DAYS_IN_WEEK - 1 - getWeekColumn(lastOfMonth, weekStartsOn)

      const gridStart = addCalendarDays(firstOfMonth, -leadingCount)
      const cellCount = leadingCount + parseCalendarDate(lastOfMonth).getDate() + trailingCount

      const days: Array<CalendarDay> = []
      for (let offset = 0; offset < cellCount; offset++) {
        days.push(buildDay(addCalendarDays(gridStart, offset), monthIndex, from, to, today))
      }

      return { key, year, month: monthIndex, weeks: chunkIntoWeeks(days) }
    })
}
